// apps/web/src/lib/auth.tsx — in-memory auth provider for Phase 2 plan 02-02.
//
// Per D-02, the access token lives in React state + a module-scope
// mirror (so lib/api.ts can read it without going through React). NO
// localStorage access of the token — localStorage is XSS-vulnerable.
//
// D-03 — refresh timing:
//   * Mount: the cold-start silentRefresh() lives in
//     components/RequireAuth.tsx (which only wraps /admin/*). Public
//     routes never trigger a refresh probe. If the user has a valid
//     refresh-cookie when RequireAuth mounts, the server returns a new
//     accessToken; otherwise silentRefresh() fails silently and the
//     RequireAuth guard redirects to /login.
//   * Preemptive: after every setAccess, schedule setTimeout(silentRefresh,
//     expiresIn * 1000 - 30_000) so the token rotates 30s before expiry.
//   * 401 retry: lib/api.ts wraps gqlRequest and triggers silentRefresh()
//     on UNAUTHENTICATED responses (one retry, then propagate).
//
// D-04 — logout: fires the LOGOUT_MUTATION first (so the server-side
// row revocation + cookie clearing runs), then clears the in-memory
// token + navigates to /login.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import i18n from "i18next";
import {
  gqlRequest,
  LOGOUT_DOC,
  setAccessToken,
  setSilentRefreshOptions,
  silentRefresh,
} from "./api";

const LOGIN_MUTATION = /* GraphQL */ `
  mutation LoginAuth($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      accessToken
      expiresIn
      user {
        id
        email
        role
      }
    }
  }
`;

const ME_QUERY = /* GraphQL */ `
  query MeAuth {
    me {
      id
      email
      role
    }
  }
`;

export interface AuthUser {
  id: string;
  email: string;
  role: "ADMIN" | "SERVICE" | "READONLY";
}

export interface AuthState {
  accessToken: string | null;
  expiresAt: number | null;
  user: AuthUser | null;
  hydrating: boolean;
}

interface AuthContextValue {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  consumeError: (messageKey: string) => string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface LoginResponse {
  login: {
    accessToken: string;
    expiresIn: number;
    user: AuthUser;
  };
}

const REFRESH_AHEAD_MS = 30_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    accessToken: null,
    expiresAt: null,
    user: null,
    hydrating: true,
  });
  const navigate = useNavigate();
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (refreshTimer.current !== null) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const handleReuseOrMissing = useCallback(() => {
    clearTimer();
    setAccessToken(null);
    setState({
      accessToken: null,
      expiresAt: null,
      user: null,
      hydrating: false,
    });
    navigate("/login", { replace: true });
  }, [clearTimer, navigate]);

  // Wire the silent-refresh interceptor's reuse/missing callbacks so
  // a REFRESH_TOKEN_REUSED or missing-cookie response clears the
  // in-memory token AND navigates to /login.
  useEffect(() => {
    setSilentRefreshOptions({
      onReuse: handleReuseOrMissing,
      onMissing: handleReuseOrMissing,
    });
    return () => {
      setSilentRefreshOptions({});
    };
  }, [handleReuseOrMissing]);

  // Listen for access-token events fired by silentRefresh() (api.ts
  // dispatches a CustomEvent because circular imports prevent a
  // direct callback).
  useEffect(() => {
    async function onAccessToken(ev: Event) {
      const detail = (
        ev as CustomEvent<{ accessToken: string; expiresIn: number }>
      ).detail;
      if (!detail?.accessToken) return;
      setAccessToken(detail.accessToken);
      const expiresAt = Date.now() + detail.expiresIn * 1000;
      clearTimer();

      // Read state.user BEFORE the setState so we can decide whether to
      // fire ME_QUERY. Cold-start path: prev.user is null → fetch me.
      // Preemptive path: prev.user is already populated → skip the fetch.
      let shouldFetchMe = false;
      setState((prev) => {
        shouldFetchMe = prev.user === null;
        return {
          accessToken: detail.accessToken,
          expiresAt,
          user: prev.user,
          hydrating: prev.user === null, // start hydrating if we have no user
        };
      });

      if (shouldFetchMe) {
        try {
          const data = await gqlRequest<{ me: AuthUser }>(ME_QUERY, {});
          setState((prev) => ({
            ...prev,
            user: data.me,
            hydrating: false,
          }));
        } catch {
          // ME_QUERY failed (UNAUTHENTICATED, network error, deleted
          // user, …). Reuse the silent-refresh-failure handler so the
          // user is bounced to /login and the in-memory token is cleared.
          handleReuseOrMissing();
          return;
        }
      }

      const delay = detail.expiresIn * 1000 - REFRESH_AHEAD_MS;
      if (delay > 0) {
        refreshTimer.current = setTimeout(() => {
          void silentRefresh();
        }, delay);
      }
    }
    if (typeof window !== "undefined") {
      window.addEventListener("gastio:access-token", onAccessToken);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("gastio:access-token", onAccessToken);
      }
      clearTimer();
    };
  }, [clearTimer, handleReuseOrMissing]);

  // Silent refresh on mount (D-03 cold start) now lives in
  // components/RequireAuth.tsx — the cold-start trigger must NOT
  // fire for public routes. RequireAuth wraps /admin/* only.

  const login = useCallback(async (email: string, password: string) => {
    const data = await gqlRequest<LoginResponse>(LOGIN_MUTATION, {
      email,
      password,
    });
    const { accessToken, expiresIn, user } = data.login;
    setAccessToken(accessToken);
    setState({
      accessToken,
      expiresAt: Date.now() + expiresIn * 1000,
      user,
      hydrating: false,
    });
  }, []);

  const logout = useCallback(async () => {
    // Fire LOGOUT_MUTATION first so the server-side row revocation +
    // cookie clearing runs. We swallow errors — local clear must
    // happen regardless (D-04 defense in depth).
    try {
      await gqlRequest<{ logout: boolean }>(LOGOUT_DOC, {});
    } catch {
      /* ignore — local clear runs either way */
    }
    clearTimer();
    setAccessToken(null);
    setState({
      accessToken: null,
      expiresAt: null,
      user: null,
      hydrating: false,
    });
    navigate("/login", { replace: true });
  }, [clearTimer, navigate]);

  const refresh = useCallback(async () => {
    await silentRefresh();
  }, []);

  const consumeError = useCallback((messageKey: string): string => {
    // Map the server's machine-friendly messageKey through i18next.
    // The server may send `auth.invalidCredentials`; the web's
    // i18n bundle uses the same key.
    if (messageKey && i18n.exists(messageKey)) {
      return i18n.t(messageKey);
    }
    return messageKey;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, login, logout, refresh, consumeError }),
    [state, login, logout, refresh, consumeError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

// Re-export ME_QUERY for components that need to fetch the current
// user (e.g. after a refresh that didn't return a user).
export { ME_QUERY };
